import { useRef, useState } from 'react'

import { ReloadOutlined } from '@ant-design/icons'
import { useCreation } from 'ahooks'
import { Button, Checkbox, Flex, Popconfirm, Space } from 'antd'
import { createStyles } from 'antd-style'

import { queryAuctionUserPageApi } from '@eastcoal/api/trade/auctionOperation/queryAuctionUserPage'
import { ProfileUser } from '@eastcoal/api/trade/interface'
import { saveUnfamiliarApi } from '@eastcoal/api/trade/unfamiliar/saveUnfamiliar'

import { RemoteTransfer } from '@eastcoal/common/components/RemoteTransfer'
import { RemoteTransferStoreRef } from '@eastcoal/common/components/RemoteTransfer/Store/interface'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'

import { MemberStatusTag } from '@eastcoal/shared/components/MemberStatusTag'


import {
  AuctionUserRemoteTransferProps,
  AuctionUserTransferProps
} from './interface'
import { MemberStatus } from '@eastcoal/shared/enums/MemberStatus'

const customRequestApi = async (
  ...args: Parameters<typeof queryAuctionUserPageApi>
) => {
  const res = await queryAuctionUserPageApi(...args)
  res.records?.forEach(item => {
    ;(item as any).disabled = item.memberStatus !== MemberStatus.SUCCESS
  })
  return res
}

const useStyles = createStyles(({ token }) => ({
  rightFooter: {
    paddingInline: token.paddingSM,
    paddingBlock: token.paddingXS,
    borderBlockStart: `${token.lineWidth}px solid ${token.colorBorderSecondary}`
  }
}))

const createLeftColumns = (
  onlySearchPartner: boolean,
  onRemove: (value: ProfileUser) => void
): AuctionUserRemoteTransferProps['leftColumns'] => [
  {
    dataIndex: 'memberName',
    title: '会员',
    render(_, record) {
      return (
        <Flex align='center' gap={4}>
          <MemberStatusTag memberStatus={record.memberStatus} />
          <span>{record.memberName}</span>
        </Flex>
      )
    }
  },
  {
    dataIndex: 'userName',
    title: '业务受托人'
  },
  {
    dataIndex: 'userPhone',
    title: '手机号'
  },
  {
    dataIndex: 'action',
    title: '操作',
    visible: () => onlySearchPartner,
    render: (_, record) => {
      return (
        <Popconfirm
          title='是否移出熟客名单？'
          onConfirm={() => {
            onRemove(record)
          }}
          onPopupClick={e => {
            e.stopPropagation()
          }}
        >
          <Button
            size='small'
            type='link'
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            移除
          </Button>
        </Popconfirm>
      )
    }
  }
]

const rightColumns: AuctionUserRemoteTransferProps['rightColumns'] = [
  {
    dataIndex: 'memberName',
    title: '会员'
  }
]

const filterOption: AuctionUserRemoteTransferProps['rightFilterOption'] = (
  searchVal,
  item
) => {
  return !!(
    item.memberName?.includes(searchVal) || item.userName?.includes(searchVal)
  )
}

export const AuctionUserTransfer = ({
  /* -------------------------------------------------------------------------- */
  /*                               RequestParams                               */
  /* -------------------------------------------------------------------------- */
  entrustMemberId,
  auctionId,
  kindId,

  /* -------------------------------------------------------------------------- */
  /*                          AuctionUserTransfer Props                         */
  /* -------------------------------------------------------------------------- */
  extraActions,

  /* -------------------------------------------------------------------------- */
  /*                             RemoteTransfer Props                           */
  /* -------------------------------------------------------------------------- */
  height = 300,
  ...restProps
}: AuctionUserTransferProps) => {
  const { message } = useFeedback()
  const ref = useRef<RemoteTransferStoreRef>(null)

  const [onlySearchPartner, setOnlySearchPartner] = useState(false)

  const leftActions = (
    <Space>
      <Checkbox
        key='onlySearchPartner'
        checked={onlySearchPartner}
        onChange={e => setOnlySearchPartner(e.target.checked)}
      >
        只看熟客
      </Checkbox>
      <Button
        type='text'
        onClick={() => {
          ref.current?.reload(true)
        }}
      >
        <ReloadOutlined />
      </Button>
    </Space>
  )

  const { styles } = useStyles()
  const rightFooter = (
    <Flex className={styles.rightFooter} justify='end'>
      {extraActions}
    </Flex>
  )
  const removePartner = async (record: ProfileUser) => {
    await saveUnfamiliarApi({
      entrustMemberId,
      kindId,
      participantId: record.memberId
    })
    message.success('移除操作成功！')
    ref.current?.reload(true)
  }

  const leftColumns = useCreation(
    () => createLeftColumns(onlySearchPartner, removePartner),
    [onlySearchPartner]
  )
  return (
    <RemoteTransfer
      {...restProps}
      ref={ref}
      api={customRequestApi}
      height={height}
      leftColumns={leftColumns}
      refreshDeps={[auctionId, entrustMemberId, onlySearchPartner]}
      rightColumns={rightColumns}
      rightFilterOption={filterOption}
      rightFooter={rightFooter}
      rowKey='profileId'
      titles={[leftActions, '已选择']}
      requestParams={{
        auctionId,
        entrustMemberId,
        kindId,
        frequent: onlySearchPartner
      }}
    />
  )
}
